import { toast } from 'react-toastify'
import { logNotificationToast } from 'services'

import { emitNotificationsChanged } from './notificationSync'
import { uncapturedToastIds } from './uncapturedToasts'

/**
 * Logs every toast the app shows so it survives in the bell panel.
 *
 * Hooked into react-toastify's own change feed rather than into the ~270 files that call `toast()`:
 * one subscription captures all of them, including any added later, and no call site has to know
 * notifications exist.
 */

/** Toast messages that are pure UI chatter and would only pad the list out. */
const IgnoredPatterns = [/copied to your clipboard/i]

/** Guards against double subscription when the header remounts (React strict mode, route changes). */
let unsubscribe: (() => void) | null = null

/** Last message per key, to swallow the duplicate a double-click produces. */
const recent = new Map<string, number>()
const DuplicateWindowMs = 10 * 1000

/** react-toastify content is a ReactNode; pull readable text out of whatever was passed. */
const toText = (node: any): string => {
  if (node === null || node === undefined || typeof node === 'boolean') return ''
  if (typeof node === 'string' || typeof node === 'number') return String(node)
  if (Array.isArray(node)) return node.map(toText).join(' ')
  if (typeof node === 'object' && node.props) return toText(node.props.children)
  return ''
}

const levelOf = (type?: string) => {
  if (type === 'success' || type === 'warning' || type === 'error' || type === 'info') return type
  // react-toastify's `default` has no severity of its own.
  return 'info'
}

export const startToastCapture = () => {
  if (unsubscribe) return unsubscribe

  unsubscribe = toast.onChange((item: any) => {
    // `added` is the moment it appears; `updated` and `removed` are the same message again.
    if (item?.status !== 'added') return
    // A loading spinner toast is not an event - its resolution, which arrives as `updated`, is.
    if (item?.isLoading) return
    // Opted out at the call site because something else already records this event.
    if (typeof item?.id === 'string' && uncapturedToastIds.has(item.id)) return

    const text = toText(item.content).replace(/\s+/g, ' ').trim()
    if (!text) return
    if (IgnoredPatterns.some((pattern) => pattern.test(text))) return

    const level = levelOf(item.type)
    const key = `toast:${level}:${text}`.slice(0, 200)

    const now = Date.now()
    const last = recent.get(key)
    if (last && now - last < DuplicateWindowMs) return
    recent.set(key, now)
    // The map is only a short-lived dedupe window, so drop what has aged out of it.
    recent.forEach((at, mapKey) => {
      if (now - at > DuplicateWindowMs) recent.delete(mapKey)
    })

    logNotificationToast({
      title: text.slice(0, 120),
      // Only carry a body when the message is longer than the title can show.
      message: text.length > 120 ? text : '',
      level,
      dedupeKey: key,
      meta: { path: window.location.pathname },
    })
      .then(() => emitNotificationsChanged())
      .catch(() => {
        // Never let logging a toast surface an error of its own - that would toast about a toast.
      })
  })

  return unsubscribe
}

export const stopToastCapture = () => {
  unsubscribe?.()
  unsubscribe = null
  recent.clear()
}
